import { EventChannel } from "redux-saga";
import { call, fork, put, take, takeLatest } from "redux-saga/effects";
import { Socket } from "socket.io-client";

import { pushMessages } from "@store/messages/messagesSlice";
import { setNotification } from "@store/ui/uiSlice";
import { userMutationSuccess } from "@store/user/userSlice";
import { Notification, User } from "@types-runway/user";
import { selectState } from "@store/selectState";

import * as socketActions from "./socketActions";
import * as SocketChannel from "./socketChannel";

export function* socketWatcher(socketChannel: EventChannel<Socket>) {
  try {
    while (true) {
      try {
        // Receives sent events.
        const event: SocketChannel.SocketEvent<Notification> = yield take(socketChannel);
        /* Depending on the event received, we should be able to address
         * the reducer with the right data.
         */

        if (event.eventName === SocketChannel.SocketEvents.MESSAGE_CREATED) {
          yield put(pushMessages(event.payload.message));
          yield put(setNotification({ message: true }));

          // See "Inbound Event Handling" in docs/architecture/messaging-realtime.md.
          const currentUser: User = yield selectState<User>((state) => state.user.data);
          yield put(
            userMutationSuccess({
              ...currentUser,
              id_requested: event.payload.id_requested,
              driver_license_id: event.payload.driver_license_id,
            })
          );
        }
      } catch (err) {
        console.log(err);
        // Handle error.
      }
    }
  } finally {
    // Runs when this task is cancelled (e.g. superseded by a new
    // RESTORE_CONNECTION via takeLatest below) -- actually removes the
    // listener registered on the old socket instead of leaking it.
    socketChannel.close();
  }
}

function* writeSocket(socket: Socket) {
  while (true) {
    const {
      event: { eventName, payload },
    } = yield take(socketActions.WEBSOCKET_SEND);

    if (eventName === SocketChannel.SocketEvents.DISCONNECT) {
      socket.emit(SocketChannel.SocketEvents.SOCKET_WITHDRAWAL, payload);
      socket.disconnect();
    } else {
      socket.emit(eventName, payload);
    }
  }
}

export function* setSocketConnection(authToken: string) {
  // Yield socket connection.
  const socket: Socket = yield call(SocketChannel.createWebSocketConnection, authToken);
  const socketChannel: EventChannel<Socket> = yield call(SocketChannel.createSocketChannel, socket);
  // Call the event watcher.
  yield fork(socketWatcher, socketChannel);
  // Have the socket emitter ready.
  yield fork(writeSocket, socket);
}

function* restoreSocketConnection({ authToken }: socketActions.RestoreConnection) {
  yield call(setSocketConnection, authToken);
}

export default function* socketSaga() {
  yield takeLatest(socketActions.RESTORE_CONNECTION, restoreSocketConnection);
}
